import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Plus, Search } from 'lucide-react';
import {
  operatorConsole,
  type PackBuilderSort,
  type PackValidation,
} from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { AddItemsModal } from '../items/AddItemsModal';
import { PackContentsGrid } from '../items/PackContentsGrid';
import { PackHealthPanel } from '../items/PackHealthPanel';
import {
  SORT_OPTIONS,
  asSort,
  filtersFromSearchParams,
  pageFromSearchParams,
  writeFilters,
  type InventoryFilterValues,
} from '../items/inventoryFilters';
import { usePackItems } from '../items/usePackItems';
import {
  SUPPORT_DISABLED_HINT,
  isSupportDisabled,
  skipReasonLabel,
} from '../packBuilderFormat';
import type { PackTabProps } from './packTabProps';

function cards(n: number): string {
  return `${n} ${n === 1 ? 'card' : 'cards'}`;
}

/**
 * The pack's contents: every card the pack holds, read from Postgres, whatever
 * happened to it afterwards. A card a player pulled is still a member — it just
 * carries a different status — so this list can never disagree with the header
 * count, and the tab reconciles the header to it on every load.
 *
 * Searching the inventory is a different job with a different source, so it
 * lives in its own mode (`?add=1`) over Elasticsearch.
 */
export function ItemsTab({ pack, limits, currency, onPackChange, notify }: PackTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const pickerPage = pageFromSearchParams(searchParams);
  const adding = searchParams.get('add') === '1';

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<PackBuilderSort>('fmv_desc');
  const [page, setPage] = useState(1);
  const [contentsVersion, setContentsVersion] = useState(0);
  const [pickerVersion, setPickerVersion] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState<PackValidation | null>(null);
  const [validating, setValidating] = useState(true);

  const contentsQuery = useMemo(() => ({ q: query, sort }), [query, sort]);
  const contents = usePackItems(pack.id, contentsQuery, page, contentsVersion);
  const packCurrency = pack.currency ?? currency;
  const readOnly = isSupportDisabled(pack);
  const searching = query.trim() !== '';
  const total = contents.data?.meta.total_count ?? null;

  // Selection belongs to one result set — any new page or search drops it.
  useEffect(() => {
    setSelected([]);
  }, [query, sort, page]);

  // The header count and this list are the same number by construction: the
  // list total is the pack's membership, so it wins. Only an unfiltered list
  // says anything about the whole pack.
  useEffect(() => {
    if (searching || total === null || pack.items_count === total) return;
    onPackChange({ ...pack, items_count: total });
  }, [searching, total, pack, onPackChange]);

  useEffect(() => {
    let cancelled = false;
    setValidating(true);
    operatorConsole.packBuilder
      .validate(pack.id, {})
      .then((report) => {
        if (!cancelled) setValidation(report);
      })
      .catch((err: unknown) => {
        if (!cancelled) notify('error', err instanceof Error ? err.message : 'Validation failed');
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pack.id, notify]);

  // `replace` on purpose: the picker's filters and page are derived screen
  // state, not navigation. Every keystroke rewrites the query; pushing would
  // bury the previous page under one entry per letter.
  const setFilters = useCallback(
    (next: InventoryFilterValues) =>
      setSearchParams(writeFilters(searchParams, next, 1), { replace: true }),
    [searchParams, setSearchParams],
  );

  const setPickerPage = useCallback(
    (next: number) =>
      setSearchParams(writeFilters(searchParams, filters, next), { replace: true }),
    [searchParams, filters, setSearchParams],
  );

  const openAdd = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('add', '1');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeAdd = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  async function handleAdd(ids: number[]) {
    setBusy(true);
    try {
      const res = await operatorConsole.packBuilder.addItems(pack.id, ids);
      // The add response carries a fresh report — no second round trip.
      setValidation(res.validation);
      onPackChange({ ...pack, items_count: pack.items_count + res.added.length });
      setContentsVersion((v) => v + 1);
      setPickerVersion((v) => v + 1);
      const reasons = [...new Set(res.skipped.map((s) => skipReasonLabel(s.reason)))].join(', ');
      const skipped = res.skipped.length ? ` · ${res.skipped.length} skipped (${reasons})` : '';
      notify(res.added.length ? 'success' : 'info', `Added ${cards(res.added.length)}${skipped}`);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not add the cards');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(ids: number[]) {
    setBusy(true);
    try {
      const res = await operatorConsole.packBuilder.removeItems(pack.id, ids);
      setValidation(res.validation);
      onPackChange({ ...pack, items_count: Math.max(0, pack.items_count - res.removed.length) });
      setSelected([]);
      setContentsVersion((v) => v + 1);
      setPickerVersion((v) => v + 1);
      // `skipped` (not_in_pack) is optional on remove — CONTRACT §4.10.
      const skippedRows = res.skipped ?? [];
      const reasons = [...new Set(skippedRows.map((s) => skipReasonLabel(s.reason)))].join(', ');
      const skipped = skippedRows.length ? ` · ${skippedRows.length} skipped (${reasons})` : '';
      notify(
        res.removed.length ? 'success' : 'info',
        `Removed ${cards(res.removed.length)}${skipped}`,
      );
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not remove the cards');
    } finally {
      setBusy(false);
    }
  }

  const count = searching
    ? `${total ?? 0} of ${cards(pack.items_count)}`
    : cards(total ?? pack.items_count);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-2">
          <label className="relative min-w-[200px] flex-1">
            <Search
              size={14}
              aria-hidden="true"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--co-muted)]"
            />
            <input
              type="search"
              aria-label="Search this pack"
              placeholder="Search the cards in this pack…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] py-1.5 pl-7 pr-2 text-[12px] text-[var(--co-text)] outline-none placeholder:text-[var(--co-muted)] focus:border-[var(--co-brand)]"
            />
          </label>
          <Select
            aria-label="Sort contents"
            value={sort}
            options={SORT_OPTIONS}
            onChange={(v) => {
              setSort(asSort(v));
              setPage(1);
            }}
          />
          <span className="text-[11px] text-[var(--co-muted)]">{count}</span>
          <Button
            onClick={openAdd}
            disabled={readOnly}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-[12px]"
          >
            <Plus size={14} aria-hidden="true" />
            Add cards
          </Button>
        </div>

        <PackContentsGrid
          items={contents.data?.items ?? []}
          loading={contents.loading}
          error={contents.error}
          meta={contents.data?.meta ?? null}
          page={page}
          onPageChange={setPage}
          selected={selected}
          onSelectedChange={setSelected}
          currency={packCurrency}
          busy={busy}
          maxPerRequest={limits.max_items_per_request}
          readOnly={readOnly}
          readOnlyHint={SUPPORT_DISABLED_HINT}
          emptyLabel={
            searching ? 'No card in this pack matches that search.' : 'This pack has no cards yet.'
          }
          onRemove={(ids) => void handleRemove(ids)}
        />
      </div>

      <PackHealthPanel validation={validation} loading={validating} currency={packCurrency} />

      {adding && (
        <AddItemsModal
          packId={pack.id}
          filters={filters}
          page={pickerPage}
          version={pickerVersion}
          currency={packCurrency}
          maxPerRequest={limits.max_items_per_request}
          busy={busy}
          readOnly={readOnly}
          readOnlyHint={SUPPORT_DISABLED_HINT}
          onFiltersChange={setFilters}
          onPageChange={setPickerPage}
          onAdd={(ids) => void handleAdd(ids)}
          onClose={closeAdd}
        />
      )}
    </div>
  );
}
